import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import CustomDropdown, { CustomDropdownOption } from '../Components/Dropdown';
import CustomMultiSelect from '../Components/CustomMultiSelect';
import { LocationCategoriesConfig } from '../api/mapConfigApi';
import { BuildingTypeOption, GroupTagOption } from '../hooks/mapOverview/useMapOverview';
import { BuildingType } from '../types/BuildingTypeEnums';
import './FilterPanel.css';

const MAX_VISIBLE_PILLS = 2;

export interface FilterPanelProps {
    locationCategories: LocationCategoriesConfig | null | undefined;
    categoryOptions: CustomDropdownOption[][];
    selectedCategory1: string;
    selectedCategory2: string;
    selectedCategory3: string;
    selectedCategory4: string;
    selectedCategory5: string;
    onSelectCategory: (level: number, key: string) => void;
    buildingTypeOptions: BuildingTypeOption[];
    selectedBuildingTypes: BuildingType[];
    onBuildingTypesChange: (value: BuildingType[]) => void;
    groupTagOptions: GroupTagOption[];
    selectedGroupTagKeys: string[];
    onGroupTagKeysChange: (value: string[]) => void;
    hasAnyFilters: boolean;
    onResetAllFilters: () => void;
}

/**
 * Collapsible filter panel shown below the search field. Collapsed, it
 * summarizes active filters as gray pills; expanded, it shows the full
 * set of filter controls.
 */
const FilterPanel: React.FC<FilterPanelProps> = ({
    locationCategories,
    categoryOptions,
    selectedCategory1,
    selectedCategory2,
    selectedCategory3,
    selectedCategory4,
    selectedCategory5,
    onSelectCategory,
    buildingTypeOptions,
    selectedBuildingTypes,
    onBuildingTypesChange,
    groupTagOptions,
    selectedGroupTagKeys,
    onGroupTagKeysChange,
    hasAnyFilters,
    onResetAllFilters,
}) => {
    const [expanded, setExpanded] = useState(false);

    const [
        category1Options,
        category2Options,
        category3Options,
        category4Options,
        category5Options,
    ] = categoryOptions;

    const categoryLevels = locationCategories
        ? [
            { levelNumber: 1, label: locationCategories.labels.category1, selectedKey: selectedCategory1, options: category1Options, disabled: false },
            { levelNumber: 2, label: locationCategories.labels.category2, selectedKey: selectedCategory2, options: category2Options, disabled: !selectedCategory1 },
            { levelNumber: 3, label: locationCategories.labels.category3, selectedKey: selectedCategory3, options: category3Options, disabled: !selectedCategory2 },
            { levelNumber: 4, label: locationCategories.labels.category4, selectedKey: selectedCategory4, options: category4Options, disabled: !selectedCategory3 },
            { levelNumber: 5, label: locationCategories.labels.category5, selectedKey: selectedCategory5, options: category5Options, disabled: !selectedCategory4 },
        ].filter((level) => level.label)
        : [];

    // Only the levels the user can currently reach — a level stays hidden
    // (rather than shown disabled) until its parent has a selection.
    const accessibleCategoryLevels = categoryLevels.filter((level) => !level.disabled);

    // ── Collapsed-state pill summary ─────────────────────────────
    // Only filters that differ from their "show everything" default get a
    // pill — e.g. "Byggnadstyp: 5/5" (nothing narrowed) is superfluous.
    const buildingTypeChanged = selectedBuildingTypes.length !== buildingTypeOptions.length;
    const groupTagChanged = selectedGroupTagKeys.length !== groupTagOptions.length;

    const pills = [
        ...(buildingTypeChanged
            ? [{
                key: 'buildingType',
                label: 'Byggnadstyp',
                valueText: `${selectedBuildingTypes.length}/${buildingTypeOptions.length}`,
            }]
            : []),
        ...(groupTagOptions.length > 0 && groupTagChanged
            ? [{
                key: 'groupTag',
                label: 'Byggnadsgrupp',
                valueText: `${selectedGroupTagKeys.length}/${groupTagOptions.length}`,
            }]
            : []),
        ...categoryLevels
            .filter((level) => level.selectedKey)
            .map((level) => ({
                key: `category-${level.label}`,
                label: level.label,
                valueText: level.options.find((option) => option.key === level.selectedKey)?.name ?? '',
            })),
    ];

    const visiblePills = pills.slice(0, MAX_VISIBLE_PILLS);
    const overflowCount = pills.length - visiblePills.length;

    return (
        <div className="filter-panel">
            <button
                type="button"
                className="filter-panel-header"
                onClick={() => setExpanded((prev) => !prev)}
                aria-expanded={expanded}
            >
                <i className="pi pi-filter filter-panel-icon" />

                {expanded ? (
                    <span className="filter-panel-title">Filter</span>
                ) : hasAnyFilters ? (
                    <div className="filter-panel-pills">
                        {visiblePills.map((pill) => (
                            <span key={pill.key} className="filter-pill">
                                {pill.label}: {pill.valueText}
                            </span>
                        ))}
                        {overflowCount > 0 && (
                            <span className="filter-pill-overflow">+{overflowCount}</span>
                        )}
                    </div>
                ) : (
                    <span className="filter-panel-title">Visar alla byggnader</span>
                )}

                <i className={`pi pi-chevron-${expanded ? 'up' : 'down'} filter-panel-chevron`} />
            </button>

            {expanded && (
                <div className="filter-panel-body">
                    <div className="filter-panel-controls">
                        <CustomMultiSelect
                            label="Byggnadstyp"
                            options={buildingTypeOptions}
                            value={selectedBuildingTypes}
                            onChange={onBuildingTypesChange}
                            maxSelectedLabels={1}
                            selectedItemsLabel="{0} typer"
                            placeholder="Välj byggnadstyper"
                            showFilterBadge={true}
                        />

                        {groupTagOptions.length > 0 && (
                            <CustomMultiSelect
                                label="Byggnadsgrupp"
                                options={groupTagOptions}
                                value={selectedGroupTagKeys}
                                onChange={onGroupTagKeysChange}
                                maxSelectedLabels={1}
                                selectedItemsLabel="{0} grupper"
                                placeholder="Välj byggnadsgrupper"
                                showFilterBadge={true}
                            />
                        )}

                        {accessibleCategoryLevels.map((level) => (
                            <CustomDropdown
                                key={level.label}
                                label={level.label}
                                options={level.options}
                                selectedKey={level.selectedKey}
                                onSelect={(key) => onSelectCategory(level.levelNumber, key)}
                            />
                        ))}
                    </div>

                    <Button
                        className="filter-panel-clear"
                        variant="secondary"
                        disabled={!hasAnyFilters}
                        onClick={() => {
                            onResetAllFilters();
                            setExpanded(false);
                        }}
                    >
                        <i className="pi pi-times-circle" style={{ marginRight: '8px' }} />
                        Rensa filter
                    </Button>
                </div>
            )}
        </div>
    );
};

export default FilterPanel;
